import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import AchBankFields, {
  achBankReady,
  type AchAccountType,
} from '../../../Components/AchBankFields';
import {
  completePortalPayflowSession,
  createPortalRecurringPayflowSession,
  enrollPortalACHRecurring,
  getPortalLoanHome,
  getPortalPayflowSession,
  updatePortalRecurringAmount,
} from '../../../endpoints/portalLoans';
import type { PortalPayflowSessionResponse } from '../../../types/portalLoans';
import { getApiErrorMessage, useApiGet } from '../../../utils/httpClientTs';
import PayflowHostedCardFrame, {
  payflowDeclineMessage,
  type PayflowHostedMessage,
} from '../../../Components/PayflowHostedCardFrame';
import { formatPortalCurrency } from '../mock/store';
import { ELF_TEAL, elfPrimaryButtonSx } from '../elfTheme';

const isActiveRecurring = (status?: string | null): boolean =>
  (status || '').trim().toUpperCase() === 'ACTIVE';

const defaultBillingDay = (
  nextPaymentDue: string | null | undefined
): number => {
  if (!nextPaymentDue) return 15;
  const day = Number.parseInt(nextPaymentDue.slice(8, 10), 10);
  if (!Number.isFinite(day) || day < 1) return 15;
  return Math.min(day, 28);
};

const LoanAutopay: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useApiGet(
    ['portal-loans-me'],
    getPortalLoanHome,
    { retry: 1 }
  );

  const enrolled = isActiveRecurring(data?.payment_info.recurring?.status);
  const totalBalance = data?.loan_info.total_balance ?? 0;
  const nextAmt = data?.payment_info.next_payment_amount;
  const currentAmount = data?.payment_info.recurring?.amount;

  const [amount, setAmount] = useState('');
  const [billingDay, setBillingDay] = useState('15');
  const [session, setSession] = useState<PortalPayflowSessionResponse | null>(
    null
  );
  const [sessionPending, setSessionPending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [waitingConfirm, setWaitingConfirm] = useState(false);
  const finalizeInFlightRef = useRef(false);
  const waitStartedRef = useRef(0);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [doneMode, setDoneMode] = useState<'enroll' | 'update' | null>(null);
  const [enrollMethod, setEnrollMethod] = useState<'card' | 'bank'>('card');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState<AchAccountType>('checking');

  useEffect(() => {
    if (!data || doneMode) return;
    if (enrolled) {
      const amt = currentAmount;
      setAmount(
        amt != null && !Number.isNaN(amt) && amt > 0 ? amt.toFixed(2) : ''
      );
    } else {
      setAmount(nextAmt != null && nextAmt > 0 ? nextAmt.toFixed(2) : '');
      setBillingDay(
        String(defaultBillingDay(data.payment_info.next_payment_due))
      );
    }
  }, [data, enrolled, currentAmount, nextAmt, doneMode]);

  const parsedAmount = Number.parseFloat(amount);
  const parsedDay = Number.parseInt(billingDay, 10);
  const amountValid =
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    parsedAmount <= totalBalance + 0.009;
  const dayValid =
    Number.isFinite(parsedDay) && parsedDay >= 1 && parsedDay <= 28;

  const bankReady = achBankReady(routingNumber, accountNumber, accountType);

  const handleEnrollBank = async () => {
    setErrorMsg(null);
    if (!amountValid || !dayValid || !bankReady) {
      setErrorMsg(
        'Enter an amount greater than 0 and at most your current balance, a billing day from 1–28, and a valid bank account.'
      );
      return;
    }
    setSessionPending(true);
    try {
      await enrollPortalACHRecurring({
        amount: Math.round(parsedAmount * 100) / 100,
        billing_day: parsedDay,
        routing_number: routingNumber.trim(),
        account_number: accountNumber.trim(),
        account_type: accountType,
      });
      setDoneMode('enroll');
      await queryClient.invalidateQueries({ queryKey: ['portal-loans-me'] });
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err) || 'Unable to enroll bank autopay.');
    } finally {
      setSessionPending(false);
    }
  };

  const handleStartEnroll = async () => {
    setErrorMsg(null);
    if (!amountValid || !dayValid) {
      setErrorMsg(
        'Enter an amount greater than 0 and at most your current balance, and a billing day from 1–28.'
      );
      return;
    }
    setSessionPending(true);
    setSession(null);
    try {
      const resp = await createPortalRecurringPayflowSession(
        Math.round(parsedAmount * 100) / 100,
        parsedDay
      );
      waitStartedRef.current = Date.now();
      setWaitingConfirm(false);
      setSession(resp);
    } catch (err) {
      setErrorMsg(
        getApiErrorMessage(err) || 'Unable to start autopay enrollment.'
      );
    } finally {
      setSessionPending(false);
    }
  };

  const finalize = useCallback(async () => {
    if (!session || finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setCompleting(true);
    setErrorMsg(null);
    try {
      await completePortalPayflowSession(session.session_id);
      setDoneMode('enroll');
      await queryClient.invalidateQueries({ queryKey: ['portal-loans-me'] });
    } catch (err) {
      setErrorMsg(
        getApiErrorMessage(err) ||
          'Waiting for PayPal to confirm this card. Do not enter it again.'
      );
    } finally {
      setCompleting(false);
      finalizeInFlightRef.current = false;
    }
  }, [session, queryClient]);

  useEffect(() => {
    if (!session || doneMode) return;
    let stopped = false;
    const tick = async () => {
      if (stopped || finalizeInFlightRef.current) return;
      if (
        waitStartedRef.current &&
        Date.now() - waitStartedRef.current > 45000
      ) {
        setWaitingConfirm(true);
      }
      try {
        const st = await getPortalPayflowSession(session.session_id);
        if (stopped) return;
        if (st.status === 'completed' || st.profile_id) {
          setDoneMode('enroll');
          await queryClient.invalidateQueries({
            queryKey: ['portal-loans-me'],
          });
          return;
        }
        if (
          st.status === 'declined' ||
          st.status === 'failed' ||
          st.status === 'expired'
        ) {
          setErrorMsg(payflowDeclineMessage(st.respmsg, st.result));
          setSession(null);
          return;
        }
        if (st.status === 'approved' || st.has_pnref) {
          void finalize();
        }
      } catch {
        // Keep polling until PayPal confirms.
      }
    };
    const id = window.setInterval(() => void tick(), 2000);
    void tick();
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [session, doneMode, queryClient, finalize]);

  const onPayflowMessage = useCallback(
    (msg: PayflowHostedMessage) => {
      if (msg.status === 'approved') {
        void finalize();
        return;
      }
      if (msg.status === 'canceled') {
        setErrorMsg('Card entry was canceled.');
        setSession(null);
        return;
      }
      setErrorMsg(payflowDeclineMessage(msg.respmsg, msg.result));
      setSession(null);
    },
    [finalize]
  );

  const handleUpdateAmount = async () => {
    setErrorMsg(null);
    if (!amountValid) {
      setErrorMsg(
        'Enter an amount greater than 0 and at most your current balance.'
      );
      return;
    }
    setSaving(true);
    try {
      await updatePortalRecurringAmount(Math.round(parsedAmount * 100) / 100);
      setDoneMode('update');
      await queryClient.invalidateQueries({ queryKey: ['portal-loans-me'] });
    } catch (err) {
      setErrorMsg(
        getApiErrorMessage(err) || 'Unable to update autopay amount.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ELF_TEAL }} />
      </Box>
    );
  }

  if (isError && !data) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        {getApiErrorMessage(error) || 'Unable to load your loan account.'}
      </Alert>
    );
  }

  if (doneMode) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          {doneMode === 'update'
            ? 'Autopay amount updated'
            : 'Autopay enrolled'}
        </Typography>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {doneMode === 'update'
              ? 'Your recurring payment amount has been updated.'
              : enrollMethod === 'bank'
                ? 'Your bank account is on file for monthly autopay.'
                : 'Your card is on file for monthly autopay.'}
          </Alert>
          <Button
            component={RouterLink}
            to="/portal/loans"
            variant="contained"
            sx={elfPrimaryButtonSx}
          >
            Back to loan home
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {enrolled ? 'Update autopay amount' : 'Set up autopay'}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {enrolled
          ? 'Change the amount billed each month on your account on file. To cancel autopay, contact ELF.'
          : 'Enroll a card or bank account for monthly autopay. Billing starts on the next eligible date for the day you choose. To cancel later, contact ELF.'}
      </Typography>

      {errorMsg && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMsg(null)}
        >
          {errorMsg}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Current balance {formatPortalCurrency(totalBalance)} (amount cannot
          exceed this)
        </Typography>

        <TextField
          label="Monthly amount"
          type="number"
          size="small"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0.01, step: '0.01' }}
          disabled={!!session || sessionPending || completing || saving}
          sx={{ mb: 2, maxWidth: 220, display: 'block' }}
        />

        {!enrolled && (
          <TextField
            label="Billing day (1–28)"
            type="number"
            size="small"
            value={billingDay}
            onChange={(e) => setBillingDay(e.target.value)}
            inputProps={{ min: 1, max: 28, step: 1 }}
            disabled={!!session || sessionPending || completing}
            helperText="Start date is set automatically."
            sx={{ mb: 2, maxWidth: 220, display: 'block' }}
          />
        )}

        {enrolled && data?.payment_info.recurring && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Billing day {data.payment_info.recurring.day_of_month ?? '—'}
            {data.payment_info.recurring.account_last4
              ? ` · ····${data.payment_info.recurring.account_last4}`
              : ''}
            . The account and billing day cannot be changed here.
          </Typography>
        )}

        {enrolled ? (
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => void handleUpdateAmount()}
              disabled={!amountValid || saving}
              sx={elfPrimaryButtonSx}
            >
              {saving ? 'Saving…' : 'Save amount'}
            </Button>
            <Button onClick={() => navigate('/portal/loans')} disabled={saving}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <>
            {!session && (
              <>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={enrollMethod}
                  onChange={(_, value) => value && setEnrollMethod(value)}
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="card">Card</ToggleButton>
                  <ToggleButton value="bank">Bank account</ToggleButton>
                </ToggleButtonGroup>
                {enrollMethod === 'bank' && (
                  <Box sx={{ mb: 2, maxWidth: 420 }}>
                    <AchBankFields
                      routingNumber={routingNumber}
                      accountNumber={accountNumber}
                      accountType={accountType}
                      onRoutingNumber={setRoutingNumber}
                      onAccountNumber={setAccountNumber}
                      onAccountType={setAccountType}
                      disabled={sessionPending || completing}
                    />
                  </Box>
                )}
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={() =>
                      void (enrollMethod === 'bank'
                        ? handleEnrollBank()
                        : handleStartEnroll())
                    }
                    disabled={
                      !amountValid ||
                      !dayValid ||
                      sessionPending ||
                      completing ||
                      (enrollMethod === 'bank' && !bankReady)
                    }
                    sx={elfPrimaryButtonSx}
                  >
                    {enrollMethod === 'bank'
                      ? sessionPending
                        ? 'Enrolling…'
                        : 'Enroll bank account'
                      : sessionPending
                        ? 'Starting checkout…'
                        : 'Enter card details'}
                  </Button>
                  <Button
                    onClick={() => navigate('/portal/loans')}
                    disabled={sessionPending || completing}
                  >
                    Cancel
                  </Button>
                </Stack>
              </>
            )}

            {session && (
              <Box sx={{ mt: 2 }}>
                {completing && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Finalizing autopay enrollment…
                  </Alert>
                )}
                {waitingConfirm && !completing && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Still waiting for PayPal confirmation. Do not enter your
                    card again.
                  </Alert>
                )}
                <Alert severity="warning" sx={{ mb: 2 }}>
                  If PayPal showed a receipt, stay on this page. Starting over
                  can enroll twice.
                </Alert>
                <PayflowHostedCardFrame
                  iframeUrl={session.iframe_url}
                  sessionId={session.session_id}
                  onMessage={onPayflowMessage}
                />
                <Button
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setErrorMsg(
                      'Go back only if you have not submitted the card. Starting over can enroll twice.'
                    );
                    setSession(null);
                  }}
                  disabled={completing}
                >
                  Cancel card entry
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LoanAutopay;
